import {
  Ast,
  AstNodeType,
  CellStatuses,
  CellValue,
  DetailedCellError,
  Group,
  HyperFormula,
  IdeasFile,
  IdeasFileNotFound,
  RoiFrame,
  RowIdentifier,
  Sheets,
  SimpleCellAddress,
} from "@inscopix/ideas-hyperformula";
import { immer } from "zustand/middleware/immer";
import { CellAddress, DataTableStoreState } from "./DataTableProvider.types";
import assert from "assert";
import { isDefined } from "utils/isDefined";
import { chain, set } from "lodash";
import { isNullish } from "utils/isNullish";
import { initSpreadsheetEngine } from "./engine";

/** Represents the `get` function used to read a Zustand store */
type GetStoreState = Parameters<
  Parameters<typeof immer<DataTableStoreState, [], []>>[0]
>[1];

/** Represents the `set` function used to modify a Zustand store */
type SetStoreState = Parameters<
  Parameters<typeof immer<DataTableStoreState, [], []>>[0]
>[0];

/**
 * Get the IDEAS formula stored in a cell at a specified address.
 *
 * This is different from HyperFormula's conception of a cell formula.
 * @param engine
 * @param address
 * @returns The IDEAS formula of the specified cell.
 */
const getIdeasCellFormula = (
  engine: HyperFormula,
  address: SimpleCellAddress,
): string | null => {
  const content = engine.getCellSerialized(address);

  // Coerce nullish value to empty formulas
  if (isNullish(content) || content === IdeasFileNotFound) {
    return null;
  }

  // Strings do not need to be modified
  if (typeof content === "string") {
    return content;
  }

  // Coerce numbers and booleans to strings
  if (typeof content === "number" || typeof content === "boolean") {
    return content.toString();
  }

  if (content instanceof Date) {
    return null;
  }

  // Groups can only be created through the =JOIN() formula
  if (content instanceof Group) {
    return null;
  }

  // Convert files to file constructor formulas
  if (content instanceof IdeasFile) {
    const fileId = content.attrs.id;
    return `=FILE("${fileId}")`;
  }

  // Convert ROI frames to ROI frame constructor formulas
  if (content instanceof RoiFrame) {
    const shapes = JSON.stringify(content.shapes).replace(/"/g, '\\"');
    return `=ROI_FRAME("${shapes}")`;
  }

  // Convert row identifiers to row identifier constructor formulas
  if (content instanceof RowIdentifier) {
    const { tableKey, rowIndex } = content;
    return `=ROW_IDENTIFIER("${tableKey}", ${rowIndex})`;
  }

  // Convert cell statuses to cell statuses constructor formulas
  if (content instanceof CellStatuses) {
    const serialized = content.statuses.join(",");
    return `=CELL_STATUSES(${serialized})`;
  }

  // If the CellValue type is expanded and this function is not updated, this
  // variable assignment will throw a compiler error, notifying us that we have
  // an unhandled case.

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const exhaustiveCheck: never = content;

  // This error will never be thrown but it appeases the type-checker
  throw new Error("Unhandled cell value type");
};

/**
 * Traverses a HyperFormula abstract syntax tree (AST), calling a specified
 * function on every node in the tree.
 * @param ast AST to traverse.
 * @param callback Function called on every node in the AST.
 */
export const traverseAst = (ast: Ast, callback: (node: Ast) => void) => {
  // Apply callback on the root node
  callback(ast);

  // Apply callback on every child node
  switch (ast.type) {
    // Leaf nodes with no child nodes
    case AstNodeType.CELL_RANGE:
    case AstNodeType.CELL_REFERENCE:
    case AstNodeType.COLUMN_RANGE:
    case AstNodeType.EMPTY:
    case AstNodeType.ERROR:
    case AstNodeType.ERROR_WITH_RAW_INPUT:
    case AstNodeType.NAMED_EXPRESSION:
    case AstNodeType.NUMBER:
    case AstNodeType.ROW_RANGE:
    case AstNodeType.STRING:
      break;

    // Arrays with child nodes stored in "args" property
    case AstNodeType.ARRAY:
      ast.args.forEach((arg) => {
        arg.forEach((node) => {
          traverseAst(node, callback);
        });
      });
      break;

    // Binary operations with child nodes stored in "left" and "right"
    // properties
    case AstNodeType.CONCATENATE_OP:
    case AstNodeType.DIV_OP:
    case AstNodeType.EQUALS_OP:
    case AstNodeType.GREATER_THAN_OP:
    case AstNodeType.GREATER_THAN_OR_EQUAL_OP:
    case AstNodeType.LESS_THAN_OP:
    case AstNodeType.LESS_THAN_OR_EQUAL_OP:
    case AstNodeType.MINUS_OP:
    case AstNodeType.NOT_EQUAL_OP:
    case AstNodeType.PLUS_OP:
    case AstNodeType.POWER_OP:
    case AstNodeType.TIMES_OP:
      traverseAst(ast.left, callback);
      traverseAst(ast.right, callback);
      break;

    // Function calls with child nodes stored in "args" property
    case AstNodeType.FUNCTION_CALL:
      ast.args.forEach((arg) => {
        traverseAst(arg, callback);
      });
      break;

    // Operations with child node stored in "value" property
    case AstNodeType.MINUS_UNARY_OP:
    case AstNodeType.PERCENT_OP:
    case AstNodeType.PLUS_UNARY_OP:
      traverseAst(ast.value, callback);
      break;

    // Operations with child node stored in "expression" property
    case AstNodeType.PARENTHESIS:
      traverseAst(ast.expression, callback);
      break;
  }
};

/**
 * Represents a column default formula or custom cell formula that was changed
 * as the result of a table operation
 */
export class SideEffects {
  #engine: HyperFormula;
  #getStoreState: GetStoreState;
  #setStoreState: SetStoreState;
  #columnEngine: HyperFormula;

  #map = {
    columns: {} as {
      [tableId: string]: {
        [columnId: string]: {
          tableType: "data" | "analysis";
          defaultFormula: string;
        };
      };
    },
    cells: {} as {
      [tableId: string]: {
        [columnId: string]: {
          [rowId: string]: {
            tableType: "data" | "analysis";
            formula: string;
          };
        };
      };
    },
  };

  constructor(
    engine: HyperFormula,
    getStoreState: GetStoreState,
    setStoreState: SetStoreState,
  ) {
    this.#engine = engine;
    this.#getStoreState = getStoreState;
    this.#setStoreState = setStoreState;
    this.#columnEngine = this.initColumnEngine();
  }

  /**
   * Creates a duplicate HyperFormula engine that only stores column default
   * formulas. This is kept in sync with the main engine and used to calculate
   * when column default formulas change as a result of a table operation.
   * @returns The column default formula engine.
   */
  initColumnEngine() {
    const sheets: Sheets = chain(this.#getStoreState().tables)
      .keyBy((table) => table.key)
      .mapValues((table) => {
        const formulas = table.columns.map((c) => c.default_formula ?? "");
        return [formulas];
      })
      .value();

    const columnEngine = initSpreadsheetEngine(sheets, this.#getStoreState);

    this.#engine.on("sheetAdded", (sheetName) => {
      columnEngine.addSheet(sheetName);
    });

    this.#engine.on("sheetRemoved", (sheetName) => {
      const sheetId = columnEngine.getSheetId(sheetName);
      assert(isDefined(sheetId));
      columnEngine.removeSheet(sheetId);
    });

    this.#engine.on("sheetRenamed", (oldName, newName) => {
      const sheetId = columnEngine.getSheetId(oldName);
      assert(isDefined(sheetId));
      columnEngine.renameSheet(sheetId, newName);
    });

    this.#engine.on("columnsAdded", (sheetName, indexes) => {
      const sheetId = columnEngine.getSheetId(sheetName);
      assert(isDefined(sheetId));
      columnEngine.addColumns(sheetId, indexes);
    });

    this.#engine.on("columnsRemoved", (sheetName, indexes) => {
      const sheetId = columnEngine.getSheetId(sheetName);
      assert(isDefined(sheetId));
      columnEngine.removeColumns(sheetId, indexes);
    });

    this.#engine.on("columnsMoved", (sheetName, ...args) => {
      const sheetId = columnEngine.getSheetId(sheetName);
      assert(isDefined(sheetId));
      columnEngine.moveColumns(sheetId, ...args);
    });

    return columnEngine;
  }

  /**
   * Calculates side-effects caused by changes to column default formulas.
   * @param callback An optional callback that can be used to modify the default
   * formula beyond HyperFormula's default rules.
   */
  calculateColumnSideEffects(
    callback?: (formula: string, sheetId: number) => string,
  ) {
    this.#getStoreState().tables.forEach((table) => {
      table.columns.forEach((column, colIdx) => {
        if (column.default_formula !== null) {
          const sheetId = this.#columnEngine.getSheetId(table.key);
          assert(isDefined(sheetId));
          const defaultFormula = column.default_formula;
          let newDefaultFormula =
            this.#columnEngine.getCellSerialized({
              sheet: sheetId,
              col: colIdx,
              row: 0,
            }) ?? "";
          assert(typeof newDefaultFormula === "string");

          // If a callback is provided, use it to modify the default formula
          if (callback) {
            newDefaultFormula = callback(newDefaultFormula, sheetId);
          }

          if (defaultFormula !== newDefaultFormula) {
            set(this.#map.columns, [table.id, column.id], {
              tableType: table.kind,
              defaultFormula: newDefaultFormula,
            });
          }
        }
      });
    });
  }

  /**
   * Generates an initial set of side-effects from every cell whose formula
   * stored in the HyperFormula engine does not match its formula stored in the
   * Zustand store.
   *
   * @param skip An optional callback function that skips side-effect
   * calculation for specified cells and/or columns.
   */
  init(
    skip?: (
      effect:
        | { type: "cell"; tableId: string; columnId: string; rowId: string }
        | { type: "column"; tableId: string; columnId: string },
    ) => boolean,
  ) {
    // Calculate cell-level side-effects
    Object.entries(this.#engine.getAllSheetsDimensions()).forEach(
      ([sheetName]) => {
        const sheetId = this.#engine.getSheetId(sheetName);
        assert(isDefined(sheetId));
        const table = this.#getStoreState().tables.find(
          ({ key }) => key === sheetName,
        );
        assert(isDefined(table));
        table.rows.forEach((_, rowIdx) => {
          table.columns.forEach((_, colIdx) => {
            const address = { sheet: sheetId, col: colIdx, row: rowIdx };
            const formula = getIdeasCellFormula(this.#engine, address) ?? "";
            if (isDefined(formula)) {
              const column = table.columns[colIdx];
              const row = table.rows[rowIdx];
              const cell = row.cells[colIdx];

              if (cell.formula !== formula) {
                if (cell.isCustomFormula) {
                  set(this.#map.cells, [table.id, column.id, row.id], {
                    tableType: table.kind,
                    formula: formula,
                  });
                }
              }
            }
          });
        });
      },
    );

    // Calculate column-level side-effects
    this.calculateColumnSideEffects();

    // Unset any side-effects that should be skipped
    if (skip) {
      Object.keys(this.#map.cells).forEach((tableId) => {
        Object.keys(this.#map.cells[tableId]).forEach((columnId) => {
          Object.keys(this.#map.cells[tableId][columnId]).forEach((rowId) => {
            const shouldSkip = skip({
              type: "cell",
              tableId,
              columnId,
              rowId,
            });

            if (shouldSkip) {
              delete this.#map.cells[tableId][columnId][rowId];
            }
          });
        });
      });

      Object.keys(this.#map.columns).forEach((tableId) => {
        Object.keys(this.#map.columns[tableId]).forEach((columnId) => {
          const shouldSkip = skip({
            type: "column",
            tableId,
            columnId,
          });

          if (shouldSkip) {
            delete this.#map.columns[tableId][columnId];
          }
        });
      });
    }
  }

  /**
   * Added a `FILE(Text)` formula for each file to a cell formula
   *
   * The updated formulas are stored as side-effects.
   * @param fileReferenceIds
   * @params address
   * @returns The updated formula.
   */
  addFiles = (fileReferenceIds: string[], address: SimpleCellAddress) => {
    // Read current formula
    const formula = this.#engine.getCellFormula(address);

    // If the cell does not contain a formula, overwrite it
    if (formula === undefined) {
      const fileFormulas = fileReferenceIds.map((id) => `FILE("${id}")`);
      if (fileFormulas.length === 0) {
        return "";
      } else if (fileFormulas.length === 1) {
        return `=${fileFormulas[0]}`;
      } else {
        return `=GROUP(${fileFormulas.join(",")})`;
      }
    }

    // Parse the formula as an AST
    const { ast } = this.#engine.formulaToAst(formula, address.sheet);

    // Check whether the current formula is an IdeasFile constructor
    const isFileFormula =
      ast.type === AstNodeType.FUNCTION_CALL && ast.procedureName === "FILE";

    // If the current formula is a IdeasFile constructor, create group with the
    // old and new file
    if (isFileFormula) {
      return this.#engine.astToFormula({
        type: AstNodeType.FUNCTION_CALL,
        procedureName: "GROUP",
        args: [
          ast,
          ...fileReferenceIds.map(
            (id) =>
              ({
                type: AstNodeType.FUNCTION_CALL,
                procedureName: "FILE",
                args: [
                  {
                    type: AstNodeType.STRING,
                    value: id,
                  },
                ],
              }) satisfies Ast,
          ),
        ],
      });
    }

    // Check whether the current formula is a Group constructor
    const isGroupFormula =
      ast.type === AstNodeType.FUNCTION_CALL && ast.procedureName === "GROUP";

    // If the current formula is a Group constructor, add the file to the group
    if (isGroupFormula) {
      fileReferenceIds.forEach((id) => {
        ast.args.push({
          type: AstNodeType.FUNCTION_CALL,
          procedureName: "FILE",
          args: [
            {
              type: AstNodeType.STRING,
              value: id,
            },
          ],
        });
      });
      return this.#engine.astToFormula(ast);
    }

    // If the current formula is neither an IdeasFile nor a Group constructor,
    // overwrite it as an IdeasFile constructor
    const fileFormulas = fileReferenceIds.map((id) => `FILE("${id}")`);
    if (fileFormulas.length === 0) {
      return "";
    } else if (fileFormulas.length === 1) {
      return `=${fileFormulas[0]}`;
    } else {
      return `=GROUP(${fileFormulas.join(",")})`;
    }
  };

  /**
   * Removes every `=FILE(Text)` referencing a specified file.
   *
   * The updated formulas are stored as side-effects.
   * @param fileReferenceIds
   * @returns An array of every cell whose formula changed as a result of this
   * operation.
   */
  deleteFiles = (fileReferenceIds: string[], address?: CellAddress) => {
    const updatedCells: {
      address: SimpleCellAddress;
      newFormula: string;
    }[] = [];

    /**
     * Resets a formula if it is a file constructor.
     * @param formula
     * @param sheetId
     * @returns The updated formula.
     */
    const updateFormula = (formula: string, sheetId: number) => {
      // Do not modify formula if not formatted as HyperFormula formula
      if (!this.#engine.validateFormula(formula)) {
        return formula;
      }

      const { ast } = this.#engine.formulaToAst(formula, sheetId);

      const isFileFormula =
        ast.type === AstNodeType.FUNCTION_CALL &&
        ast.procedureName === "FILE" &&
        ast.args[0].type === AstNodeType.STRING &&
        fileReferenceIds.includes(ast.args[0].value);

      const isGroupFormula =
        ast.type === AstNodeType.FUNCTION_CALL &&
        ast.procedureName === "GROUP" &&
        ast.args.some((arg) => {
          return (
            arg.type === AstNodeType.FUNCTION_CALL &&
            arg.procedureName === "FILE" &&
            arg.args[0].type === AstNodeType.STRING &&
            fileReferenceIds.includes(arg.args[0].value)
          );
        });

      if (isFileFormula) {
        return "";
      } else if (isGroupFormula) {
        ast.args = ast.args.filter((arg) => {
          return !(
            arg.type === AstNodeType.FUNCTION_CALL &&
            arg.procedureName === "FILE" &&
            arg.args[0].type === AstNodeType.STRING &&
            fileReferenceIds.includes(arg.args[0].value)
          );
        });
        return ast.args.length > 0 ? this.#engine.astToFormula(ast) : "";
      } else {
        return formula;
      }
    };

    if (address === undefined) {
      Object.entries(this.#engine.getAllSheetsDimensions()).forEach(
        ([sheetName]) => {
          const sheetId = this.#engine.getSheetId(sheetName);
          assert(isDefined(sheetId));
          const table = this.#getStoreState().tables.find(
            ({ key }) => key === sheetName,
          );
          assert(isDefined(table));

          table.rows.forEach((_, rowIdx) => {
            table.columns.forEach((_, colIdx) => {
              const address = { sheet: sheetId, col: colIdx, row: rowIdx };
              const formula = getIdeasCellFormula(this.#engine, address) ?? "";
              if (isDefined(formula)) {
                const newFormula = updateFormula(formula, sheetId);
                const column = table.columns[colIdx];
                const row = table.rows[rowIdx];
                const cell = row.cells[colIdx];

                // Formulas should not be updated for executed analysis table rows
                const shouldUpdateFormula =
                  table.kind === "data" ||
                  (table.kind === "analysis" && row.editable);

                if (newFormula !== formula && shouldUpdateFormula) {
                  updatedCells.push({
                    address: { sheet: sheetId, col: colIdx, row: rowIdx },
                    newFormula: newFormula,
                  });

                  if (cell.isCustomFormula) {
                    set(this.#map.cells, [table.id, column.id, row.id], {
                      tableType: table.kind,
                      formula: newFormula,
                    });
                  }
                }
              }
            });
          });
        },
      );
    } else {
      const { tableId, columnId, rowId } = address;
      const table = this.#getStoreState().tables.find(
        ({ id }) => id === tableId,
      );
      assert(isDefined(table));
      const sheetId = this.#engine.getSheetId(table.key);
      assert(isDefined(sheetId));
      const colIdx = table.columns.findIndex(({ id }) => id === columnId);
      assert(colIdx !== -1);
      const rowIdx = table.rows.findIndex(({ id }) => id === rowId);
      assert(rowIdx !== -1);
      const column = table.columns[colIdx];
      const row = table.rows[rowIdx];
      const cell = row.cells[colIdx];
      const newFormula = updateFormula(cell.formula, sheetId);

      // Formulas should not be updated for executed analysis table rows
      const shouldUpdateFormula =
        table.kind === "data" || (table.kind === "analysis" && row.editable);

      if (newFormula !== cell.formula && shouldUpdateFormula) {
        updatedCells.push({
          address: { sheet: sheetId, col: colIdx, row: rowIdx },
          newFormula: newFormula,
        });

        if (cell.isCustomFormula) {
          set(this.#map.cells, [table.id, column.id, row.id], {
            tableType: table.kind,
            formula: newFormula,
          });
        }
      }
    }

    return updatedCells;
  };

  /**
   * Calculate side-effects caused by mapping analysis parameters.
   * @param tableId The ID of the analysis table where parameters are being
   * mapped.
   * @param sourceTableIds The table IDs from which parameters values are read.
   */
  updateAnalysisParameterSourceColumns(
    tableId: string,
    sourceTableIds: string[],
  ) {
    // Create a copy of the engine
    const sheets = this.#engine.getAllSheetsSerialized();
    const engineCopy = HyperFormula.buildFromSheets(sheets);

    // Calculate sheet ID
    const analysisTable = this.#getStoreState().tables.find(
      ({ id }) => id === tableId,
    );
    assert(isDefined(analysisTable));
    const sheetId = engineCopy.getSheetId(analysisTable.key);
    assert(isDefined(sheetId));

    // Get column group storing source columns
    const sourcesGroup = analysisTable.columnGroups.find((group) => {
      return group.definition?.kind === "sources";
    });
    assert(isDefined(sourcesGroup));

    // Get source columns
    const sourceColumns = analysisTable.columns
      .map((column, order) => [column, order] as const)
      .filter(([column]) => column.group === sourcesGroup.id);

    // Find sources columns referencing tables that are no longer source
    // tables
    const deletedSourceColumns = sourceColumns.filter(([column]) => {
      return (
        column.definition.kind !== "identifier" ||
        !sourceTableIds.includes(column.definition.table.id)
      );
    });

    // Calculate the table IDs for which new source columns will need to be
    // created
    const newSourceTableIds = sourceTableIds.filter((tableId) => {
      return !sourceColumns.some(([column]) => {
        return (
          column.definition.kind === "identifier" &&
          column.definition.table.id === tableId
        );
      });
    });

    // Calculate the order position of new source columns. If the table does not
    // contain any existing source columns, new columns will be created after
    // the last pinned column.
    const firstPinnedColumnIdx = analysisTable.columns.findIndex(
      (c) => c.pinned,
    );
    const startIndex =
      firstPinnedColumnIdx +
      (sourceColumns.length - deletedSourceColumns.length);

    // Notify engine
    engineCopy.batch(() => {
      // Remove deleted source columns
      if (deletedSourceColumns.length > 0) {
        engineCopy.removeColumns(
          sheetId,
          ...deletedSourceColumns.map(
            ([_, order]) => [order, 1] satisfies [number, number],
          ),
        );

        // Since we are using copy of the engine, we need to manually notify the
        // column engine
        const columnEngineSheetId = this.#columnEngine.getSheetId(
          analysisTable.key,
        );
        assert(isDefined(columnEngineSheetId));
        this.#columnEngine.removeColumns(
          columnEngineSheetId,
          ...deletedSourceColumns.map(
            ([_, order]) => [order, 1] satisfies [number, number],
          ),
        );
      }

      // Add new source columns
      if (newSourceTableIds.length > 0) {
        engineCopy.addColumns(sheetId, [
          startIndex + 1,
          newSourceTableIds.length,
        ]);

        // Since we are using copy of the engine, we need to manually notify the
        // column engine
        const columnEngineSheetId = this.#columnEngine.getSheetId(
          analysisTable.key,
        );
        assert(isDefined(columnEngineSheetId));
        this.#columnEngine.addColumns(columnEngineSheetId, [
          startIndex + 1,
          newSourceTableIds.length,
        ]);
      }
    });

    // Add/remove source columns from a copy of the column store state. This is
    // used to create a lookup map from old column order positions to new
    // positions.
    let newColumns = analysisTable.columns;
    // Remove deleted columns
    newColumns = newColumns.filter((column) => {
      return !deletedSourceColumns.some(
        ([deletedColumn]) => column.id === deletedColumn.id,
      );
    });
    newColumns.splice(
      startIndex + 1,
      0,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
      ...newSourceTableIds.map(() => ({ id: "" }) as unknown as any),
    );

    // Create a lookup map from old column positions to new column positions.
    // Elements with undefined values represent source columns that were
    // deleted.
    const columnOrderConversionMap: (number | undefined)[] = [];
    analysisTable.columns.forEach((column, oldIdx) => {
      const newIdx = newColumns.findIndex(({ id }) => id === column.id);
      if (newIdx !== -1) {
        columnOrderConversionMap[newIdx] = oldIdx;
      }
    });

    // Add side-effects to lookup map
    Object.entries(engineCopy.getAllSheetsDimensions()).forEach(
      ([sheetName, { height, width }]) => {
        const sheetId = engineCopy.getSheetId(sheetName);
        assert(isDefined(sheetId));
        const columnEngineSheetId = this.#columnEngine.getSheetId(sheetName);
        assert(isDefined(columnEngineSheetId));
        const table = this.#getStoreState().tables.find(
          ({ key }) => key === sheetName,
        );
        assert(isDefined(table));

        table.rows.forEach((_, rowIdx) => {
          table.columns.forEach((_, colIdx) => {
            const address = { sheet: sheetId, col: colIdx, row: rowIdx };
            const formula = getIdeasCellFormula(engineCopy, address) ?? "";

            if (isDefined(formula)) {
              // Lookup the old column index before source columns were
              // added/removed
              const oldColIdx =
                sheetName === analysisTable.key
                  ? columnOrderConversionMap[colIdx]
                  : colIdx;

              // Do nothing if the column was removed
              if (oldColIdx === undefined) {
                return;
              }

              const column = table.columns[oldColIdx];
              const row = table.rows[rowIdx];
              const cell = row.cells[oldColIdx];

              const defaultFormula =
                getIdeasCellFormula(this.#columnEngine, {
                  sheet: columnEngineSheetId,
                  col: colIdx,
                  row: 0,
                }) ?? "";

              // Calculate column-level side-effects
              if ((column.default_formula ?? "") !== defaultFormula) {
                set(this.#map.columns, [table.id, column.id], {
                  tableType: table.kind,
                  defaultFormula: defaultFormula,
                });
              }

              if (cell.formula !== formula) {
                if (cell.isCustomFormula) {
                  set(this.#map.cells, [table.id, column.id, row.id], {
                    tableType: table.kind,
                    formula: formula,
                  });
                }
              }
            }
          });
        });
      },
    );
  }

  /**
   * Replaces the table key in every `=ROW_IDENTIFIER(Text, Number)` formula
   * with a new key.
   *
   * The updated formulas are stored as side-effects.
   * @param prevTableKey
   * @param newTableKey
   * @returns An array of every cell whose formula changed as a result of this
   * operation.
   */
  updateRowIdentifiers(prevTableKey: string, newTableKey: string) {
    const updatedCells: {
      address: SimpleCellAddress;
      newFormula: string;
    }[] = [];

    /**
     * Replaces the table key in a formula if present.
     * @param formula
     * @param sheetId
     * @returns The updated formula.
     */
    const updateFormula = (formula: string, sheetId: number) => {
      // Do not modify formula if not formatted as HyperFormula formula
      if (!this.#engine.validateFormula(formula)) {
        return formula;
      }

      const { ast } = this.#engine.formulaToAst(formula, sheetId);

      // Update all row identifiers to use new table key
      traverseAst(ast, (node) => {
        if (
          node.type === AstNodeType.FUNCTION_CALL &&
          node.procedureName === "ROW_IDENTIFIER" &&
          node.args[0].type === AstNodeType.STRING &&
          node.args[0].value === prevTableKey
        ) {
          node.args[0].value = newTableKey;
        }
      });

      return this.#engine.astToFormula(ast);
    };

    Object.entries(this.#engine.getAllSheetsDimensions()).forEach(
      ([sheetName]) => {
        const sheetId = this.#engine.getSheetId(sheetName);
        assert(isDefined(sheetId));
        const table = this.#getStoreState().tables.find(
          ({ key }) => key === sheetName,
        );
        assert(isDefined(table));

        table.rows.forEach((_, rowIdx) => {
          table.columns.forEach((_, colIdx) => {
            const address = { sheet: sheetId, col: colIdx, row: rowIdx };
            const formula = getIdeasCellFormula(this.#engine, address) ?? "";
            if (isDefined(formula)) {
              const newFormula = updateFormula(formula, sheetId);
              const column = table.columns[colIdx];
              const row = table.rows[rowIdx];
              const cell = row.cells[colIdx];

              if (newFormula !== formula) {
                updatedCells.push({
                  address: { sheet: sheetId, col: colIdx, row: rowIdx },
                  newFormula,
                });

                if (cell.isCustomFormula) {
                  set(this.#map.cells, [table.id, column.id, row.id], {
                    tableType: table.kind,
                    formula: newFormula,
                  });
                }
              }
            }
          });
        });
      },
    );

    // Calculate column-level side-effects
    this.calculateColumnSideEffects(updateFormula);

    return updatedCells;
  }

  /**
   * Converts all formulas in a specified row to static formulas. The updated
   * formulas are stored as side-effects.
   *
   * Static formulas do not contain cell references and thus will always
   * evaluate to the same cell values regardless of other cell values in the
   * table.
   * @param tableId
   * @param rowId
   * @returns
   */
  convertToStaticFormulas(tableId: string, rowId: string) {
    const updatedCells: {
      address: SimpleCellAddress;
      newFormula: string;
    }[] = [];

    // Parse row
    const table = this.#getStoreState().tables.find(({ id }) => id === tableId);
    assert(isDefined(table));
    const sheetId = this.#engine.getSheetId(table.key);
    assert(isDefined(sheetId));
    const rowIdx = table.rows.findIndex(({ id }) => id === rowId);
    const row = table.rows[rowIdx];

    // Convert cell values to static formulas
    row.cells.forEach((cell, colIdx) => {
      const column = table.columns[colIdx];
      if (column.editable) {
        const staticFormula = convertCellValueToStaticFormula(cell.value);
        set(this.#map.cells, [table.id, column.id, row.id], {
          tableType: table.kind,
          formula: staticFormula,
        });

        updatedCells.push({
          address: { sheet: sheetId, col: colIdx, row: rowIdx },
          newFormula: staticFormula,
        });
      }
    });

    return updatedCells;
  }

  /**
   * Updates the formulas in the Zustand store from currently calculated
   * side-effects.
   */
  applyOnStoreState() {
    this.#setStoreState((state) => {
      // Update column default formulas
      Object.entries(this.#map.columns).forEach(([tableId, i]) => {
        const table = state.tables.find(({ id }) => id === tableId);
        assert(isDefined(table));
        Object.entries(i).forEach(([columnId, { defaultFormula }]) => {
          const colIdx = table.columns.findIndex(({ id }) => id === columnId);
          const column = table.columns[colIdx];
          assert(isDefined(column));
          column.default_formula = defaultFormula;

          table.rows.forEach((row) => {
            if (!row.cells[colIdx].isCustomFormula) {
              row.cells[colIdx].formula = defaultFormula;
            }
          });
        });
      });

      // Update custom cell formulas
      Object.entries(this.#map.cells).forEach(([tableId, i]) => {
        const table = state.tables.find(({ id }) => id === tableId);
        assert(isDefined(table));
        Object.entries(i).forEach(([columnId, j]) => {
          const colIdx = table.columns.findIndex(({ id }) => id === columnId);
          Object.entries(j).forEach(([rowId, { formula }]) => {
            const row = table.rows.find(({ id }) => id === rowId);
            assert(isDefined(row));
            row.cells[colIdx].formula = formula;
          });
        });
      });
    });
  }

  /**
   * Updates the formulas in the HyperFormula engine from currently calculated
   * side-effects, causing the engine to recalculate cell values.
   */
  applyOnEngineState() {
    this.#engine.batch(() => {
      // Update column default formulas
      Object.entries(this.#map.columns).forEach(([tableId, i]) => {
        const table = this.#getStoreState().tables.find(
          ({ id }) => id === tableId,
        );
        assert(isDefined(table));
        const sheetId = this.#engine.getSheetId(table.key);
        assert(isDefined(sheetId));

        Object.entries(i).forEach(([columnId, { defaultFormula }]) => {
          const colIdx = table.columns.findIndex(({ id }) => id === columnId);
          const column = table.columns[colIdx];
          assert(isDefined(column));
          table.rows.forEach((row, rowIdx) => {
            if (!row.cells[colIdx].isCustomFormula) {
              this.#engine.setCellContents(
                {
                  sheet: sheetId,
                  col: colIdx,
                  row: rowIdx,
                },
                defaultFormula,
              );
            }
          });
        });
      });

      // Update custom cell formulas
      Object.entries(this.#map.cells).forEach(([tableId, i]) => {
        const table = this.#getStoreState().tables.find(
          ({ id }) => id === tableId,
        );
        assert(isDefined(table));
        const sheetId = this.#engine.getSheetId(table.key);
        assert(isDefined(sheetId));

        Object.entries(i).forEach(([columnId, j]) => {
          const colIdx = table.columns.findIndex(({ id }) => id === columnId);
          assert(colIdx !== -1);
          Object.entries(j).forEach(([rowId, { formula }]) => {
            const rowIdx = table.rows.findIndex(({ id }) => id === rowId);
            assert(rowIdx !== -1);
            this.#engine.setCellContents(
              {
                sheet: sheetId,
                col: colIdx,
                row: rowIdx,
              },
              formula,
            );
          });
        });
      });
    });
  }

  /**
   * Formats the side-effects for sending in network requests.
   * @returns The serialized side-effects.
   */
  serialize() {
    const serializedSideEffects: SerializedSideEffects = {
      data_table_columns: [],
      analysis_table_columns: [],
      data_table_cells: [],
      analysis_table_cells: [],
    };

    Object.entries(this.#map.columns).forEach(([tableId, i]) => {
      Object.entries(i).forEach(([columnId, { tableType, defaultFormula }]) => {
        if (tableType === "analysis") {
          serializedSideEffects.analysis_table_columns.push({
            id: columnId,
            default_formula: defaultFormula,
          });
        } else {
          serializedSideEffects.data_table_columns.push({
            id: columnId,
            default_formula: defaultFormula,
          });
        }
      });
    });

    Object.entries(this.#map.cells).forEach(([tableId, i]) => {
      Object.entries(i).forEach(([columnId, j]) => {
        Object.entries(j).forEach(([rowId, { tableType, formula }]) => {
          if (tableType === "analysis") {
            serializedSideEffects.analysis_table_cells.push({
              project: this.#getStoreState().project.id,
              table: tableId,
              column: columnId,
              row: rowId,
              formula,
            });
          } else {
            serializedSideEffects.data_table_cells.push({
              project: this.#getStoreState().project.id,
              table: tableId,
              column: columnId,
              row: rowId,
              formula,
            });
          }
        });
      });
    });

    return serializedSideEffects;
  }
}

/** Represents operation side-effects formatted for network requests */
export type SerializedSideEffects = {
  data_table_columns: {
    id: string;
    default_formula: string;
  }[];

  analysis_table_columns: {
    id: string;
    default_formula: string;
  }[];

  data_table_cells: {
    project: string;
    table: string;
    column: string;
    row: string;
    formula: string;
  }[];

  analysis_table_cells: {
    project: string;
    table: string;
    column: string;
    row: string;
    formula: string;
  }[];
};

/**
 * Forces HyperFormula to recalculate every cell that passes a specified
 * callback condition.
 * @param engine The HyperFormula engine instance.
 * @param shouldRecalculate Callback that takes a cell value and returns a
 * boolean indicating whether the cell should be recalculated.
 */
export const recalculateCellValues = (
  engine: HyperFormula,
  shouldRecalculate: (
    address: SimpleCellAddress,
    formula: string | undefined,
    value: CellValue,
  ) => boolean,
) => {
  Object.entries(engine.getAllSheetsValues()).forEach(([sheetName, rows]) => {
    const sheetId = engine.getSheetId(sheetName);
    assert(isDefined(sheetId));
    rows.forEach((row, rowIdx) => {
      row.forEach((cellValue, colIdx) => {
        const address = { sheet: sheetId, col: colIdx, row: rowIdx };
        const formula = engine.getCellFormula(address);
        if (shouldRecalculate(address, formula, cellValue)) {
          engine.setCellContents(address, formula);
        }
      });
    });
  });
};

/**
 * Represents a valid analysis parameter value usable by the Task Execution
 * Service
 */
export type AnalysisParamValue = number | string | boolean | null;

/**
 * Converts a HyperFormula cell value into a static formula string.
 *
 * Static formulas do not contain cell references and thus will always evaluate
 * to the same cell values regardless of other cell values in the table.
 * @param value
 * @returns The static.
 */
export const convertCellValueToStaticFormula = (value: CellValue): string => {
  // Ignore values that cannot be converted
  if (
    value === null ||
    value instanceof DetailedCellError ||
    value === IdeasFileNotFound
  ) {
    return "";
  }

  // Convert strings to text formulas
  if (typeof value === "string") {
    return `'${value}`;
  }

  // Convert numbers to strings
  if (typeof value === "number") {
    return value.toString();
  }

  // Convert boolean values to boolean constructor formulas
  if (typeof value === "boolean") {
    return value ? "=TRUE()" : "=FALSE()";
  }

  // Convert file values to file constructor formulas
  if (value instanceof IdeasFile) {
    return `=FILE("${value.attrs.referenceId}")`;
  }

  // Convert row identifier values to row identifier constructor formulas
  if (value instanceof RowIdentifier) {
    return `=ROW_IDENTIFIER("${value.tableKey}", ${value.rowIndex})`;
  }

  // Convert ROI frame values to ROI frame constructor formulas
  if (value instanceof RoiFrame) {
    const serialized = JSON.stringify(value.shapes).replace(/"/g, '\\"');
    return `=ROI_FRAME("${serialized}")`;
  }

  // Convert cell status values to cell statuses constructor formulas
  if (value instanceof CellStatuses) {
    const serialized = value.statuses.join(",");
    return `=CELL_STATUSES(${serialized})`;
  }

  // Convert group values recursively to group constructor formulas
  if (value instanceof Group) {
    const nestedFormulas = value.cellValues.map((cellValue) => {
      const formula = convertCellValueToStaticFormula(cellValue);
      return formula.slice(1); // Remove leading "="
    });
    return `=GROUP(${nestedFormulas.join(", ")})`;
  }

  // Since we cannot use a switch statement here, we check that the type of
  // value is never. This ensures that when the CellValue type changes, compiler
  // errors are thrown.

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const exhaustiveCheck: never = value;
  throw new Error("Exhaustive check failed");
};

/**
 * Converts a HyperFormula cell value into a valid parameter value usable by the
 * Task Execution Service.
 *
 * If the cell value is a `Group`, it will be converted into multiple parameter
 * values.
 * @param value
 * @returns An array of analysis parameter values.
 */
export const convertCellValueToAnalysisParamValues = (
  value: CellValue,
): AnalysisParamValue[] => {
  // Ignore values that cannot be converted
  if (
    value === null ||
    value instanceof DetailedCellError ||
    value instanceof RowIdentifier ||
    value === IdeasFileNotFound
  ) {
    return [];
  }

  // Ignore empty strings
  if (typeof value === "string") {
    return value !== "" ? [value] : [];
  }

  // Scalars do not require conversion
  if (typeof value === "number" || typeof value === "boolean") {
    return [value];
  }

  // Files are converted to file IDs
  if (value instanceof IdeasFile) {
    return [value.attrs.id];
  }

  // ROI frames are converted to JSON serialized shape data
  if (value instanceof RoiFrame) {
    return [JSON.stringify(value.shapes)];
  }

  // Cell statuses are converted to an array of numbers
  if (value instanceof CellStatuses) {
    return [JSON.stringify(value.statuses)];
  }

  // Groups are converted to an array of values
  if (value instanceof Group) {
    return value.cellValues.flatMap(convertCellValueToAnalysisParamValues);
  }

  // Since we cannot use a switch statement here, we check that the type of
  // value is never. This ensures that when the CellValue type changes, compiler
  // errors are thrown.

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const exhaustiveCheck: never = value;
  throw new Error("Exhaustive check failed");
};
